/**
 * Dashboard Screen
 * Overview of patrimoine (financial state) and goals summary
 */

import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  RefreshControl,
  TouchableOpacity,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useNavigation } from '@react-navigation/native';
import { useAuthStore } from '../store/authStore';
import { useFinancialProfile } from '../hooks/useFinancialProfile';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { aiApi } from '../api/endpoints/ai';
import { budgetApi } from '../api/endpoints/budget';
import { dashboardApi } from '../api/endpoints/dashboard';
import { subscriptionsApi } from '../api/endpoints/subscriptions';
import FinancialStateCard from '../components/financial/FinancialStateCard';
import ArbitrageOpportunityCard, { ArbitrageOpportunity } from '../components/financial/ArbitrageOpportunityCard';
import PaywallModal from '../components/common/PaywallModal';
import ProgressBar from '../components/goals/ProgressBar';
import DailyBudgetCard from '../components/dashboard/DailyBudgetCard';
import OverdraftAlertBanner from '../components/dashboard/OverdraftAlertBanner';
import { useBudgetStore } from '../store/budgetStore';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';
import type { FinancialStateSummary, FinancialGoal, DashboardArbitrageData } from '../api/types';
import { useLayout } from '../components/layout/AppLayout';
import logger from '../utils/logger';

export default function DashboardScreen() {
  const navigation = useNavigation();
  const { user } = useAuthStore();
  const { profile, hasProfile, isLoading: profileLoading } = useFinancialProfile();
  const { navigateTo } = useLayout();

  // Financial state
  const [financialState, setFinancialState] = useState<FinancialStateSummary | null>(null);
  const [stateLoading, setStateLoading] = useState(true);

  // Arbitrage opportunities
  const [arbitrageData, setArbitrageData] = useState<DashboardArbitrageData | null>(null);

  // Budget summary
  const [budgetSummary, setBudgetSummary] = useState<any>(null);
  const [budgetLoading, setBudgetLoading] = useState(true);

  // Goals summary
  const [goals, setGoals] = useState<FinancialGoal[]>([]);
  const [goalsLoading, setGoalsLoading] = useState(true);

  // Refresh state
  const [refreshing, setRefreshing] = useState(false);

  // Forecast
  const { forecast, forecastLoading, loadForecast } = useBudgetStore();
  const [overdraftDismissed, setOverdraftDismissed] = useState(false);

  // Paywall state
  const [showPaywall, setShowPaywall] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [currentArbitrageGain, setCurrentArbitrageGain] = useState<number | undefined>();

  useEffect(() => {
    loadData();
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    try {
      const response = await subscriptionsApi.getCurrentSubscription();
      setIsPremium(response.subscription.is_premium);
    } catch (error) {
      logger.error('Error checking subscription:', error);
      setIsPremium(false);
    }
  };

  const loadData = async () => {
    await Promise.all([loadDashboardData(), loadBudgetSummary(), loadGoals(), loadForecast()]);
  };

  const loadDashboardData = async () => {
    try {
      setStateLoading(true);
      const response = await dashboardApi.getFinancialDashboard();
      setFinancialState(response.state_summary);
      setArbitrageData(response.arbitrage);
    } catch (error) {
      logger.error('Error loading dashboard data:', error);
      // Fallback to old API if new one fails
      try {
        const fallbackResponse = await aiApi.getFinancialState();
        setFinancialState(fallbackResponse.state_summary);
      } catch (fallbackError) {
        logger.error('Error loading fallback financial state:', fallbackError);
      }
    } finally {
      setStateLoading(false);
    }
  };

  const loadBudgetSummary = async () => {
    try {
      setBudgetLoading(true);
      const month = format(new Date(), 'yyyy-MM');
      const response = await budgetApi.getBudgetSummary(month);
      // Backend returns data directly or wrapped in summary key
      setBudgetSummary(response.summary || response);
    } catch (error) {
      logger.error('Error loading budget summary:', error);
    } finally {
      setBudgetLoading(false);
    }
  };

  const loadGoals = async () => {
    try {
      setGoalsLoading(true);
      const response = await aiApi.getFinancialGoals('active');
      setGoals(response.financial_goals || []);
    } catch (error) {
      logger.error('Error loading goals:', error);
    } finally {
      setGoalsLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  // Calculate patrimoine metrics
  const patrimoine = {
    epargne: profile?.current_savings || 0,
    dettes: profile?.debt_amount || 0,
    net: (profile?.current_savings || 0) - (profile?.debt_amount || 0),
    capaciteEpargne: profile?.monthly_saving_capacity || 0,
  };

  // Get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bonjour';
    if (hour < 18) return 'Bon après-midi';
    return 'Bonsoir';
  };

  const firstName = user?.first_name || 'Utilisateur';

  // Handle arbitrage card press
  const handleArbitragePress = (opportunity: ArbitrageOpportunity) => {
    if (isPremium) {
      // Premium users go directly to chat
      navigateTo('Chats');
    } else {
      // Freemium users see paywall
      setCurrentArbitrageGain(opportunity.annual_gain);
      setShowPaywall(true);
    }
  };

  // Handle successful subscription
  const handleSubscribeSuccess = () => {
    setIsPremium(true);
    navigateTo('Chats');
  };

  return (
    <ScrollView
        style={styles.container}
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        showsVerticalScrollIndicator={false}
      >
        {/* Header Greeting */}
      <View style={styles.header}>
        <Text style={styles.greeting}>{getGreeting()},</Text>
        <Text style={styles.userName}>{firstName} 👋</Text>
      </View>

      {/* Overdraft Alert Banner */}
      {!overdraftDismissed && forecast && forecast.projected_end_of_month < 0 && (
        <OverdraftAlertBanner
          projectedBalance={forecast.projected_end_of_month}
          onDismiss={() => setOverdraftDismissed(true)}
          onViewDetails={() => navigateTo('Chats')}
        />
      )}

      {/* Daily Budget Card */}
      <DailyBudgetCard forecast={forecast} isLoading={forecastLoading} />

      {/* Arbitrage Opportunity Card - Displayed at TOP for max impact */}
      {arbitrageData?.has_opportunity && arbitrageData.opportunities.length > 0 && (
        <ArbitrageOpportunityCard
          opportunity={arbitrageData.opportunities[0] as ArbitrageOpportunity}
          onPress={() => handleArbitragePress(arbitrageData.opportunities[0] as ArbitrageOpportunity)}
        />
      )}

      {/* Goals Summary */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Objectifs en cours</Text>
          <TouchableOpacity onPress={() => navigateTo('Goals')}>
            <Text style={styles.seeAllLink}>Voir tout</Text>
          </TouchableOpacity>
        </View>

        {goalsLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color={colors.primary} />
            <Text style={styles.loadingText}>Chargement...</Text>
          </View>
        ) : goals.length > 0 ? (
          <View style={styles.goalsList}>
            {goals.slice(0, 3).map((goal) => (
              <TouchableOpacity
                key={goal.id}
                style={styles.goalCard}
                onPress={() => navigateTo('Goals')}
              >
                <View style={styles.goalHeader}>
                  <Icon
                    name={getGoalIcon(goal.type)}
                    size={20}
                    color={colors.primary}
                  />
                  <Text style={styles.goalTitle} numberOfLines={1}>
                    {goal.title}
                  </Text>
                </View>
                <ProgressBar
                  current={goal.current_amount ?? 0}
                  target={goal.target_amount ?? 1}
                  size="small"
                />
                <View style={styles.goalFooter}>
                  <Text style={styles.goalProgress}>
                    {(goal.current_amount ?? 0).toLocaleString('fr-FR')} € / {(goal.target_amount ?? 0).toLocaleString('fr-FR')} €
                  </Text>
                  <Text style={styles.goalPercent}>
                    {Math.round(((goal.current_amount ?? 0) / (goal.target_amount || 1)) * 100)}%
                  </Text>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        ) : (
          <TouchableOpacity
            style={styles.emptyCard}
            onPress={() => navigateTo('Chats')}
          >
            <Icon name="target" size={32} color={colors.primary} />
            <Text style={styles.emptyCardTitle}>Aucun objectif actif</Text>
            <Text style={styles.emptyCardText}>
              Discutez avec Wealfi pour définir vos objectifs
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Financial State Card */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>État Financier</Text>
        {stateLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color={colors.primary} />
            <Text style={styles.loadingText}>Chargement...</Text>
          </View>
        ) : financialState ? (
          <FinancialStateCard stateSummary={financialState} showProgression={true} />
        ) : (
          <TouchableOpacity
            style={styles.emptyCard}
            onPress={() => navigation.navigate('FinancialDiagnosticWizard' as never)}
          >
            <Icon name="chart-line" size={32} color={colors.primary} />
            <Text style={styles.emptyCardTitle}>Créer mon diagnostic</Text>
            <Text style={styles.emptyCardText}>
              Complétez votre profil financier pour voir votre état
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Patrimoine Overview */}
      {hasProfile && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Patrimoine</Text>
          <View style={styles.patrimoineGrid}>
            <View style={styles.patrimoineCard}>
              <View style={[styles.patrimoineIcon, { backgroundColor: '#E8F5E9' }]}>
                <Icon name="piggy-bank-outline" size={20} color="#4CAF50" />
              </View>
              <Text style={styles.patrimoineLabel}>Épargne</Text>
              <Text style={styles.patrimoineValue}>
                {patrimoine.epargne.toLocaleString('fr-FR')} €
              </Text>
            </View>

            <View style={styles.patrimoineCard}>
              <View style={[styles.patrimoineIcon, { backgroundColor: '#FFEBEE' }]}>
                <Icon name="credit-card-outline" size={20} color="#EF5350" />
              </View>
              <Text style={styles.patrimoineLabel}>Dettes</Text>
              <Text style={styles.patrimoineValue}>
                {patrimoine.dettes.toLocaleString('fr-FR')} €
              </Text>
            </View>

            <View style={styles.patrimoineCard}>
              <View style={[styles.patrimoineIcon, { backgroundColor: '#E3F2FD' }]}>
                <Icon name="wallet-outline" size={20} color="#2196F3" />
              </View>
              <Text style={styles.patrimoineLabel}>Patrimoine net</Text>
              <Text style={[
                styles.patrimoineValue,
                patrimoine.net < 0 && styles.valueNegative,
                patrimoine.net > 0 && styles.valuePositive,
              ]}>
                {patrimoine.net.toLocaleString('fr-FR')} €
              </Text>
            </View>

            <View style={styles.patrimoineCard}>
              <View style={[styles.patrimoineIcon, { backgroundColor: '#FFF3E0' }]}>
                <Icon name="trending-up" size={20} color="#FF9800" />
              </View>
              <Text style={styles.patrimoineLabel}>Épargne mensuelle</Text>
              <Text style={[
                styles.patrimoineValue,
                patrimoine.capaciteEpargne < 0 && styles.valueNegative,
                patrimoine.capaciteEpargne > 0 && styles.valuePositive,
              ]}>
                {patrimoine.capaciteEpargne.toLocaleString('fr-FR')} €/mois
              </Text>
            </View>
          </View>
        </View>
      )}

      {/* Budget Summary */}
      {(() => {
        if (budgetLoading) return null;
        if (!budgetSummary) return null;

        const categories = budgetSummary.categories || [];
        const totalBudgeted = budgetSummary.total_budgeted || 0;
        const totalSpent = budgetSummary.total_spent_in_budgeted || 0;
        const transactionCount = budgetSummary.transaction_count || 0;
        const lastTxDate = budgetSummary.last_transaction_date
          ? parseISO(budgetSummary.last_transaction_date)
          : null;
        const percentage = totalBudgeted > 0 ? Math.round((totalSpent / totalBudgeted) * 100) : 0;
        const onTrack = categories.filter((c: any) => !c.over_budget).length;
        const overBudget = categories.filter((c: any) => c.over_budget).length;

        // Dynamic title with current month
        const monthName = format(new Date(), 'MMMM yyyy', { locale: fr });
        const budgetTitleText = `Budget ce mois (${monthName.charAt(0).toUpperCase() + monthName.slice(1)})`;

        // Coverage state
        let coverageState: 'no_data' | 'partial' | 'up_to_date' = 'up_to_date';
        if (transactionCount === 0) {
          coverageState = 'no_data';
        } else if (lastTxDate) {
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          yesterday.setHours(0, 0, 0, 0);
          if (lastTxDate < yesterday) coverageState = 'partial';
        }

        // Hide card entirely if no budget setup and no data
        if (categories.length === 0 && totalBudgeted === 0 && coverageState === 'no_data') return null;

        return (
          <View style={styles.section}>
            <TouchableOpacity
              style={styles.budgetCard}
              onPress={() => navigateTo('Budget')}
              activeOpacity={0.7}
            >
              <View style={styles.budgetHeader}>
                <View style={[styles.patrimoineIcon, { backgroundColor: '#EDE7F6' }]}>
                  <Icon name="credit-card-check-outline" size={20} color="#7C4DFF" />
                </View>
                <Text style={styles.budgetTitle}>{budgetTitleText}</Text>
              </View>

              {coverageState === 'no_data' ? (
                /* No data state — import CTA */
                <View style={styles.coverageEmptyContainer}>
                  <Icon name="file-upload-outline" size={36} color={colors.textSecondary} />
                  <Text style={styles.coverageEmptyTitle}>Pas encore de transactions</Text>
                  <Text style={styles.coverageEmptyText}>
                    Importez vos relevés bancaires pour une analyse précise
                  </Text>
                  <TouchableOpacity
                    style={styles.coverageImportButton}
                    onPress={() => (navigation as any).navigate('ImportBankStatementsWizard')}
                    activeOpacity={0.7}
                  >
                    <Icon name="file-upload-outline" size={16} color="#FFFFFF" />
                    <Text style={styles.coverageImportButtonText}>Importer mes relevés</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                /* Partial or up-to-date — show budget numbers */
                <>
                  {coverageState === 'partial' && lastTxDate && (
                    <TouchableOpacity
                      style={styles.coveragePartialBanner}
                      onPress={() => (navigation as any).navigate('ImportBankStatementsWizard')}
                      activeOpacity={0.7}
                    >
                      <Icon name="alert-circle-outline" size={16} color="#F57C00" style={{ marginRight: spacing.xs }} />
                      <Text style={styles.coveragePartialText}>
                        Analyse partielle — transactions jusqu'au {format(lastTxDate, 'd MMM', { locale: fr })}
                      </Text>
                      <Icon name="chevron-right" size={16} color="#F57C00" />
                    </TouchableOpacity>
                  )}

                  <Text style={styles.budgetAmounts}>
                    {totalSpent.toLocaleString('fr-FR')} € / {totalBudgeted.toLocaleString('fr-FR')} €{' '}
                    <Text style={styles.budgetPercent}>({percentage}%)</Text>
                  </Text>

                  <View style={styles.budgetBarBackground}>
                    <View
                      style={[
                        styles.budgetBarFill,
                        {
                          width: `${Math.min(percentage, 100)}%`,
                          backgroundColor: percentage > 100 ? '#EF5350' : percentage > 85 ? '#FF9800' : '#4CAF50',
                        },
                      ]}
                    />
                  </View>

                  <View style={styles.budgetStats}>
                    {onTrack > 0 && (
                      <Text style={styles.budgetStatText}>
                        {onTrack} catégorie{onTrack > 1 ? 's' : ''} dans les clous
                      </Text>
                    )}
                    {overBudget > 0 && (
                      <Text style={[styles.budgetStatText, { color: '#EF5350' }]}>
                        {overBudget} catégorie{overBudget > 1 ? 's' : ''} dépassée{overBudget > 1 ? 's' : ''}
                      </Text>
                    )}
                  </View>
                </>
              )}

              <View style={styles.budgetLinkRow}>
                <Text style={styles.budgetLink}>Voir détail Budget</Text>
                <Icon name="chevron-right" size={18} color={colors.primary} />
              </View>
            </TouchableOpacity>
          </View>
        );
      })()}

      {/* Quick Actions */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Actions rapides</Text>
        <View style={styles.actionsGrid}>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => navigateTo('Chats')}
          >
            <Icon name="chat-processing-outline" size={24} color={colors.primary} />
            <Text style={styles.actionText}>Parler à Wealfi</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => navigation.navigate('FinancialDiagnosticWizard' as never)}
          >
            <Icon name="refresh" size={24} color={colors.primary} />
            <Text style={styles.actionText}>Actualiser le diagnostic</Text>
          </TouchableOpacity>
        </View>
      </View>

        {/* Paywall Modal */}
      {/* Paywall Modal */}
      <PaywallModal
        visible={showPaywall}
        onClose={() => setShowPaywall(false)}
        onSubscribeSuccess={handleSubscribeSuccess}
        trigger="arbitrage"
        potentialGains={currentArbitrageGain}
      />
    </ScrollView>
  );
}

// Helper to get goal icon based on type
function getGoalIcon(type: string): string {
  const icons: Record<string, string> = {
    emergency_fund: 'shield-check-outline',
    savings: 'piggy-bank-outline',
    debt: 'credit-card-off-outline',
    investment: 'chart-line',
    income: 'trending-up',
    budget: 'calculator-variant-outline',
    default: 'target',
  };
  return icons[type] || icons.default;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  content: {
    padding: spacing.lg,
    paddingBottom: spacing.xxl,
  },
  header: {
    marginBottom: spacing.xl,
  },
  greeting: {
    fontSize: typography.fontSize.lg,
    color: colors.textSecondary,
  },
  userName: {
    fontSize: typography.fontSize.xxl,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
  },
  section: {
    marginBottom: spacing.xl,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginBottom: spacing.md,
  },
  seeAllLink: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontWeight: typography.fontWeight.medium,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
  },
  loadingText: {
    marginLeft: spacing.sm,
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  emptyCard: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.xl,
    alignItems: 'center',
  },
  emptyCardTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginTop: spacing.sm,
  },
  emptyCardText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    textAlign: 'center',
    marginTop: spacing.xs,
  },

  // Patrimoine grid
  patrimoineGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -spacing.xs,
  },
  patrimoineCard: {
    width: '50%',
    padding: spacing.xs,
  },
  patrimoineIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  patrimoineLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  patrimoineValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
  },
  valueNegative: {
    color: '#EF5350',
  },
  valuePositive: {
    color: '#4CAF50',
  },

  // Budget card
  budgetCard: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.md,
  },
  budgetHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  budgetTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  budgetAmounts: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    marginBottom: spacing.sm,
  },
  budgetPercent: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.textSecondary,
  },
  budgetBarBackground: {
    height: 8,
    backgroundColor: colors.border,
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: spacing.md,
  },
  budgetBarFill: {
    height: '100%',
    borderRadius: 4,
  },
  budgetStats: {
    gap: spacing.xs,
    marginBottom: spacing.md,
  },
  budgetStatText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  budgetLinkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  budgetLink: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.primary,
  },

  // Budget coverage states
  coverageEmptyContainer: {
    alignItems: 'center',
    paddingVertical: spacing.lg,
    gap: spacing.sm,
  },
  coverageEmptyTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  coverageEmptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  coverageImportButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    gap: spacing.xs,
    marginTop: spacing.sm,
  },
  coverageImportButtonText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: '#FFFFFF',
  },
  coveragePartialBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF8E1',
    borderRadius: 8,
    padding: spacing.sm,
    marginBottom: spacing.md,
  },
  coveragePartialText: {
    flex: 1,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium,
    color: '#F57C00',
  },

  // Goals list
  goalsList: {
    gap: spacing.sm,
  },
  goalCard: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.sm,
  },
  goalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  goalTitle: {
    flex: 1,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    color: colors.text,
  },
  goalFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: spacing.sm,
  },
  goalProgress: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  goalPercent: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.primary,
  },

  // Actions grid
  actionsGrid: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  actionButton: {
    flex: 1,
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.md,
    alignItems: 'center',
    gap: spacing.sm,
  },
  actionText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.text,
    textAlign: 'center',
  },
});
